import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { CategoryTile } from '../../components/Cards';
import { Chip, Empty, Loading, Input } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { colors } from '../../theme';

const TYPES = [
  { key: '', label: 'All' },
  { key: 'service', label: 'Services' },
  { key: 'product', label: 'Products' },
  { key: 'transport', label: 'Transport' },
  { key: 'job', label: 'Jobs' },
  { key: 'vehicle', label: 'Vehicles' },
];

// Things KAAMWALA does that are not a category - they have their own pages.
const MORE = [
  { icon: '🚕', label: 'Book a ride', to: '/ride' },
  { icon: '📦', label: 'Send a parcel', to: '/ride?type=courier' },
  { icon: '🏠', label: 'Property', to: '/property' },
  { icon: '🐄', label: 'Animals & birds', to: '/animals' },
  { icon: '🏷️', label: 'Buy & sell', to: '/buy-sell' },
  { icon: '🚗', label: 'Vehicles', to: '/vehicles' },
  { icon: '💼', label: 'Jobs', to: '/jobs' },
  { icon: '🎂', label: 'Special days', to: '/special-days' },
  { icon: '✨', label: 'Your desire', to: '/desire' },
];

/** Every KAAMWALA category, grouped by section — the full service directory. */
const ExplorePage = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');

  useEffect(() => {
    api.categories.list({ grouped: 'true' })
      .then((res) => setGroups(res.data || []))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);

  const sections = useMemo(() => groups
    .map((g) => ({
      title: g.group,
      items: (g.items || []).filter((c) => (!type || c.type === type)
        && (!query || c.name.toLowerCase().includes(query.toLowerCase()))),
    }))
    .filter((s) => s.items.length), [groups, type, query]);

  return (
    <div>
      <PageHeader title="All services" subtitle="Everything KAAMWALA can do for you" back={false} />

      <div className="card sh-sm" style={{ marginBottom: 8 }}>
        <Input
          icon="🔍"
          placeholder="Search a category — electrician, salon, groceries…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="grow"
          style={{ marginBottom: 12 }}
        />
        <div className="row no-bar" style={{ gap: 8, overflowX: 'auto' }}>
          {TYPES.map((t) => (
            <Chip key={t.key || 'all'} label={t.label} active={type === t.key} onClick={() => setType(t.key)} />
          ))}
        </div>
      </div>

      {!query && !type ? (
        <section>
          <div className="row" style={{ gap: 12, margin: '22px 0 12px' }}>
            <h3 className="h3">More on KAAMWALA</h3>
            <div className="grow" style={{ height: 1, background: colors.line }} />
          </div>
          <div className="row wrap" style={{ gap: 12 }}>
            {MORE.map((m) => (
              <button
                key={m.label}
                type="button"
                className="card press"
                onClick={() => navigate(m.to)}
                style={{ width: 110, padding: '14px 8px', textAlign: 'center', border: `1px solid ${colors.line}`, cursor: 'pointer' }}
              >
                <div style={{ fontSize: 28 }}>{m.icon}</div>
                <div className="t-tiny" style={{ fontWeight: 700, marginTop: 4 }}>{m.label}</div>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {loading ? <Loading text="Loading categories…" /> : !sections.length ? (
        <Empty emoji="🔍" title="No categories match" text="Try a different filter or search word." />
      ) : (
        sections.map((section) => (
          <section key={section.title}>
            <div className="row" style={{ gap: 12, margin: '26px 0 12px' }}>
              <h3 className="h3">{section.title}</h3>
              <div className="grow" style={{ height: 1, background: colors.line }} />
              <span className="t-tiny">{section.items.length}</span>
            </div>
            <div className="grid grid-cat">
              {section.items.map((c, i) => (
                <Reveal key={c._id} delay={stagger(i, 25)}>
                  <CategoryTile
                    category={c}
                    onClick={() => navigate(`/category/${c._id}`, { state: { category: c } })}
                  />
                </Reveal>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default ExplorePage;
